import type { StyleSpecification } from "@vietmap/vietmap-gl-js/dist/vietmap-gl";
import type { IconSet } from "../config/icon-sets";
import { rewriteLegacyStyleUrl } from "./styles-api";

export type SpriteEntry = {
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio: number;
  sdf?: boolean;
};

export type SpriteIndex = {
  atlas: Record<string, SpriteEntry>;
  /** PNG to crop icons from (same pixel ratio as `atlas`). */
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  /** The decoded PNG — icons are drawn from it on a canvas (sprite-icon.tsx). */
  image: HTMLImageElement;
};

const VIETMAP_HOST = /(\.vietmap\.vn|maps\.vnpost\.vn)/;

/** Absolute sprite base URL of a ready-made sprite set (relative → editor origin). */
export function resolveIconSetUrl(set: Extract<IconSet, { kind: "sprite" }>): string {
  if (/^https?:\/\//.test(set.url)) return set.url;
  const base = (import.meta as any).env.BASE_URL as string;
  return new URL(base + set.url.replace(/^\//, ""), window.location.origin).toString();
}

/** `<base>[@2x].json` / `.png`, keeping any query string after the suffix and
 *  adding the API key for Vietmap-hosted sprites. */
export function spriteFileUrl(base: string, ext: "json" | "png", apiKey?: string, retina = false): string {
  let url = rewriteLegacyStyleUrl(base);
  if (apiKey && VIETMAP_HOST.test(url) && !/[?&]apikey=/.test(url)) {
    url += (url.includes("?") ? "&" : "?") + "apikey=" + encodeURIComponent(apiKey);
  }
  const file = `${retina ? "@2x" : ""}.${ext}`;
  const q = url.indexOf("?");
  return q === -1 ? `${url}${file}` : `${url.slice(0, q)}${file}${url.slice(q)}`;
}

async function fetchSprite(base: string, apiKey: string | undefined, retina: boolean): Promise<SpriteIndex> {
  const jsonUrl = spriteFileUrl(base, "json", apiKey, retina);
  const imageUrl = spriteFileUrl(base, "png", apiKey, retina);
  const res = await fetch(jsonUrl);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const atlas = (await res.json()) as Record<string, SpriteEntry>;
  const image = new Image();
  image.src = imageUrl;
  await image.decode();
  return { atlas, imageUrl, imageWidth: image.naturalWidth, imageHeight: image.naturalHeight, image };
}

const cache = new Map<string, Promise<SpriteIndex>>();

/**
 * Load (and cache) a sprite's atlas + image. On high-density screens the
 * `@2x` file comes first (as MapLibre does) — our generated sprites draw it at
 * 3×, so icons stay sharp; falls back to the 1× file.
 */
export function loadSpriteIndex(base: string, apiKey?: string): Promise<SpriteIndex> {
  const key = `${base}|${apiKey ?? ""}`;
  let p = cache.get(key);
  if (!p) {
    const retina = (window.devicePixelRatio || 1) > 1;
    p = retina
      ? fetchSprite(base, apiKey, true).catch(() => fetchSprite(base, apiKey, false))
      : fetchSprite(base, apiKey, false);
    // Don't keep failures around — a retry (e.g. after entering a key) should refetch.
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return p;
}

// ── Sprite arrays ──────────────────────────────────────────────────────────
// A style's `sprite` is a URL, or (MapLibre multi-sprite) a list of
// { id, url }. Images of the "default" entry are referenced by plain name,
// others as "<id>:<name>" — MapZone data layers add their icons that way.

export type SpriteValue = string | { id: string; url: string }[] | undefined;

/** URL of the style's main ("default") sprite. */
export function defaultSpriteUrl(sprite: SpriteValue | unknown): string {
  if (typeof sprite === "string") return sprite;
  if (Array.isArray(sprite)) return (sprite.find((s) => s?.id === "default") ?? sprite[0])?.url ?? "";
  return "";
}

/** Swap the main sprite, keeping any extra (MapZone) sprites. */
export function withDefaultSprite(sprite: SpriteValue | unknown, url: string): SpriteValue {
  if (!Array.isArray(sprite)) return url;
  const extras = sprite.filter((s) => s?.id !== "default");
  return extras.length ? [{ id: "default", url }, ...extras] : url;
}

/** Add (or replace) an extra sprite entry. */
export function addSprite(sprite: SpriteValue | unknown, id: string, url: string): SpriteValue {
  const list = Array.isArray(sprite) ? sprite.filter((s) => s?.id !== id) : typeof sprite === "string" && sprite ? [{ id: "default", url: sprite }] : [];
  return [...list, { id, url }];
}

/** Drop an extra sprite entry; back to a plain URL when only "default" is left. */
export function removeSprite(sprite: SpriteValue | unknown, id: string): SpriteValue {
  if (!Array.isArray(sprite)) return sprite as SpriteValue;
  const list = sprite.filter((s) => s?.id !== id);
  return list.length === 1 && list[0].id === "default" ? list[0].url : list;
}

/** Apply `fn` to every sprite URL (string or array form). */
export function mapSpriteUrls(sprite: SpriteValue | unknown, fn: (url: string) => string): SpriteValue {
  if (typeof sprite === "string") return fn(sprite);
  if (Array.isArray(sprite)) return sprite.map((s) => ({ ...s, url: fn(s.url) }));
  return sprite as SpriteValue;
}

/** Literal icon names the style references through `icon-image`. */
export function usedIconNames(style: StyleSpecification): Set<string> {
  const names = new Set<string>();
  const walk = (v: unknown) => {
    if (typeof v === "string") {
      // Token templates like "{maki}-15" can't be resolved statically.
      // "id:name" lives in an extra sprite (e.g. MapZone layers), not the main one.
      if (v && !v.includes("{") && !v.includes(":")) names.add(v);
      return;
    }
    if (!Array.isArray(v) || typeof v[0] !== "string") return;
    const [op, ...args] = v;
    if (op === "match") {
      // ["match", input, label, out, label, out, …, fallback] → outputs only.
      args.slice(1).forEach((a, i, rest) => {
        if (i % 2 === 1 || i === rest.length - 1) walk(a);
      });
    } else if (op === "case") {
      // ["case", cond, out, cond, out, …, fallback] → outputs only.
      args.forEach((a, i) => {
        if (i % 2 === 1 || i === args.length - 1) walk(a);
      });
    } else if (op === "coalesce" || op === "image") {
      args.forEach(walk);
    }
  };
  for (const layer of style.layers || []) {
    if (layer.type !== "symbol") continue;
    walk((layer.layout as Record<string, unknown> | undefined)?.["icon-image"]);
  }
  names.delete("");
  return names;
}
